import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Instagram } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/common';

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { language, changeLanguage } = useLanguage();
  const t = translations[language].header;

  const navigation = [
    { name: t.home, path: '/' },
    { name: t.about, path: '/about' },
    { name: t.services, path: '/services' },
    { name: t.booking, path: '/booking' },
    { name: t.contact, path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 md:h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 md:space-x-3">
            <img 
              src="/logo.png" 
              alt="Logo Stef Ruiz" 
              className="h-10 w-10 md:h-12 md:w-12 object-contain flex-shrink-0"
            />
            <div data-testid="header-identity" className="flex flex-col leading-tight">
              <span data-testid="header-name" className="text-[15px] md:text-lg font-semibold text-teal-600 tracking-wide whitespace-nowrap">
                {t.name}
              </span>
              <span className="text-[10px] md:text-[11px] text-gray-600 whitespace-nowrap mt-0.5">
                {t.role}
              </span>
              <span className="text-[10px] md:text-[11px] text-gray-500 whitespace-nowrap">
                {t.specialty}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 xl:space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                  isActive(item.path)
                    ? 'text-teal-600'
                    : 'text-gray-700 hover:text-teal-600'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <Link
              to="/test"
              data-testid="nav-test-link-desktop"
              className={`text-sm font-semibold whitespace-nowrap px-4 py-1.5 rounded-full transition-all duration-200 ${
                isActive('/test')
                  ? 'bg-teal-600 text-white'
                  : 'bg-teal-50 text-teal-600 hover:bg-teal-100'
              }`}
            >
              {t.test}
            </Link>
            <a
              href="https://www.instagram.com/comprendre_qui_on_est"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-700 hover:text-pink-600 transition-colors duration-200"
              aria-label="Instagram"
            >
              <Instagram size={20} />
            </a>
            
            {/* Language Selector */}
            <div className="flex items-center space-x-1 border-l border-gray-200 pl-4">
              <button
                onClick={() => changeLanguage('fr')}
                className={`text-sm font-semibold px-2 py-1 rounded transition-all duration-200 ${
                  language === 'fr'
                    ? 'text-teal-600 bg-teal-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                FR
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => changeLanguage('es')}
                className={`text-sm font-semibold px-2 py-1 rounded transition-all duration-200 ${
                  language === 'es'
                    ? 'text-teal-600 bg-teal-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                ES
              </button>
            </div>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-teal-600 hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <nav className="px-4 pt-2 pb-4 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                  isActive(item.path)
                    ? 'text-teal-600 bg-teal-50'
                    : 'text-gray-700 hover:text-teal-600 hover:bg-gray-50'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            <Link
              to="/test"
              onClick={() => setIsOpen(false)}
              data-testid="nav-test-link-mobile"
              className={`block px-3 py-2 rounded-md text-base font-semibold transition-colors ${
                isActive('/test')
                  ? 'bg-teal-600 text-white'
                  : 'bg-teal-50 text-teal-600 hover:bg-teal-100'
              }`}
            >
              {t.test}
            </Link>

            {/* Language Selector Mobile */}
            <div className="flex items-center space-x-2 px-3 py-2">
              <span className="text-sm text-gray-600">{t.languageLabel}</span>
              <button
                onClick={() => {
                  changeLanguage('fr');
                  setIsOpen(false);
                }}
                className={`text-sm font-semibold px-3 py-1 rounded transition-all duration-200 ${
                  language === 'fr'
                    ? 'text-white bg-teal-600'
                    : 'text-gray-600 bg-gray-100'
                }`}
              >
                FR
              </button>
              <button
                onClick={() => {
                  changeLanguage('es');
                  setIsOpen(false);
                }}
                className={`text-sm font-semibold px-3 py-1 rounded transition-all duration-200 ${
                  language === 'es'
                    ? 'text-white bg-teal-600'
                    : 'text-gray-600 bg-gray-100'
                }`}
              >
                ES
              </button>
            </div>
            
            <a
              href="https://www.instagram.com/comprendre_qui_on_est"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-3 py-2 text-gray-700 hover:text-pink-600"
            >
              <Instagram size={20} className="mr-2" />
              Instagram
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
